"use client";

import { useEffect, useRef, useState } from "react";
import {
  APP_SPLASH_ELEMENT_ID,
  APP_SPLASH_MAX_HOLD_MS,
  APP_SPLASH_MIN_VISIBLE_MS,
  hideAppSplash,
  revealAppSplash,
} from "../pwa";

export interface UseAppSplashOptions {
  minVisibleMs?: number;
  splashElementId?: string;
  hold?: boolean;
  maxHoldMs?: number;
}

export interface UseAppSplashResult {
  visible: boolean;
}

export function useAppSplash({
  minVisibleMs = APP_SPLASH_MIN_VISIBLE_MS,
  splashElementId = APP_SPLASH_ELEMENT_ID,
  hold = false,
  maxHoldMs = APP_SPLASH_MAX_HOLD_MS,
}: UseAppSplashOptions = {}): UseAppSplashResult {
  const [visible, setVisible] = useState(true);
  const shownAtRef = useRef<number | null>(null);

  useEffect(() => {
    const splash = document.getElementById(splashElementId);
    if (!splash) {
      setVisible(false);
      return;
    }

    const conceal = () => {
      hideAppSplash(splash);
      // The parties skeleton is already painted under the fading gold.
      // Drop the splash status-bar treatment now so those icons turn white
      // with the ink page, instead of staying black until the fade ends.
      setVisible(false);
    };

    if (shownAtRef.current === null) {
      shownAtRef.current = Date.now();
    }

    if (hold) {
      revealAppSplash(splash, { maxHoldMs });
      setVisible(true);
      const maxTimer = window.setTimeout(() => {
        conceal();
      }, maxHoldMs);
      return () => {
        window.clearTimeout(maxTimer);
      };
    }

    const elapsed = Date.now() - shownAtRef.current;
    const wait = Math.max(0, minVisibleMs - elapsed);
    const hideTimer = window.setTimeout(() => {
      conceal();
    }, wait);

    return () => {
      window.clearTimeout(hideTimer);
    };
  }, [hold, maxHoldMs, minVisibleMs, splashElementId]);

  return { visible };
}
